import React from 'react';
import { NavLink } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Linkedin, Aperture } from 'lucide-react';
import { Button } from '../ui/Button';

const TiktokIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" /></svg>
);

export const Footer: React.FC = () => {
  return (
    <footer className="relative z-20 bg-black text-white pt-20 md:pt-32 pb-12 border-t border-white/5">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12">
        {/* Mobile Footer Content */}
        <div className="flex md:hidden flex-col items-center text-center gap-10 mb-16">
            <div>
              <h2 className="text-3xl font-display font-bold uppercase leading-tight mb-6">Visuals That Sell.<br/> <span className="text-zinc-500">Service That Delivers.</span></h2>
              <div className="flex justify-center space-x-8 items-center mt-6">
                  <a href="https://www.instagram.com/realta.media/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-brand-500 transition-all" aria-label="Instagram"><Instagram size={24} /></a>
                  <a href="https://www.facebook.com/realta.media" target="_blank" rel="noopener noreferrer" className="text-white hover:text-brand-500 transition-all" aria-label="Facebook"><Facebook size={24} /></a>
                  <a href="https://www.tiktok.com/@realta.media/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-brand-500 transition-all" aria-label="TikTok"><TiktokIcon size={24} /></a>
              </div>
            </div>
            
            <div className="flex flex-col gap-6 w-full">
                <NavLink to="/about" className="text-lg font-display font-bold uppercase text-white hover:text-brand-500 transition-all">About</NavLink>
                <NavLink to="/blog" className="text-lg font-display font-bold uppercase text-white hover:text-brand-500 transition-all">Blog</NavLink>
                <NavLink to="/listing-media" className="text-lg font-display font-bold uppercase text-white hover:text-brand-500 transition-all">Listing Media</NavLink>
                <NavLink to="/brand-media" className="text-lg font-display font-bold uppercase text-white hover:text-brand-500 transition-all">Brand Media</NavLink>
                <NavLink to="/pre-shoot-guide" className="text-lg font-display font-bold uppercase text-white hover:text-brand-500 transition-all">Pre-Shoot Guide</NavLink>
            </div>

            <div className="flex flex-col items-center gap-6 w-full">
                <div className="flex flex-col items-center gap-3">
                    <a href="tel:4376732582" className="text-2xl font-display font-bold text-white tracking-wide">4376 - REALTA</a>
                    <a href="mailto:info@realta.media" className="text-lg font-display font-medium text-zinc-400">info@realta.media</a>
                </div>
            </div>

            <div className="w-full">
                <NavLink to="/booking" className="w-full"><Button variant="outline" size="sm" className="w-full h-16 text-sm">Book Now</Button></NavLink>
            </div>
        </div>

        {/* Desktop Footer Content */}
        <div className="hidden md:flex justify-between gap-12 mb-24 items-stretch">
          <div className="flex-shrink-0 w-4/12 flex flex-col justify-between">
            <div>
                <h2 className="text-5xl font-display font-bold uppercase leading-none mb-12 max-w-md text-white">Visuals That Sell.<br/> <span className="text-zinc-400">Service That Delivers.</span></h2>
                <div>
                    <div className="flex space-x-8 items-center">
                        <a href="https://www.instagram.com/realta.media/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-brand-500 transition-all" aria-label="Instagram"><Instagram size={24} /></a>
                        <a href="https://www.facebook.com/realta.media" target="_blank" rel="noopener noreferrer" className="text-white hover:text-brand-500 transition-all" aria-label="Facebook"><Facebook size={24} /></a>
                        <a href="https://www.tiktok.com/@realta.media/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-brand-500 transition-all" aria-label="TikTok"><TiktokIcon size={24} /></a>
                    </div>
                </div>
            </div>
          </div>

          <div className="flex-grow flex flex-col justify-between">
             <div>
                <div className="flex justify-end gap-16 items-start mb-12">
                    {/* Explore Links Section */}
                    <div className="text-right space-y-4 py-2">
                        <NavLink to="/" className="text-2xl font-display font-bold uppercase text-white hover:text-brand-500 transition-all block">Home</NavLink>
                        <NavLink to="/listing-media" className="text-2xl font-display font-bold uppercase text-white hover:text-brand-500 transition-all block">Listing Media</NavLink>
                        <NavLink to="/brand-media" className="text-2xl font-display font-bold uppercase text-white hover:text-brand-500 transition-all block">Brand Media</NavLink>
                    </div>

                    {/* Vertical Divider - Fixed height to match link block exactly */}
                    <div className="w-px bg-white/20 self-stretch min-h-[220px]"></div>

                    {/* Contact Links Section */}
                    <div className="text-right flex flex-col justify-between h-full min-h-[220px] py-2">
                        <div className="flex flex-col items-end gap-6 justify-between h-full w-full">
                            <div className="flex gap-4">
                                <NavLink to="/about"><Button variant="outline" size="sm" className="h-12 px-10 text-[10px]">About</Button></NavLink>
                                <NavLink to="/contact"><Button variant="outline" size="sm" className="h-12 px-10 text-[10px]">Contact</Button></NavLink>
                            </div>
                            <div className="flex flex-col items-end gap-2 w-full">
                                <a href="tel:4376732582" className="text-3xl font-display font-bold text-white hover:text-brand-500 transition-colors tracking-wide whitespace-nowrap">4376 - REALTA</a>
                                <div className="flex flex-col items-end w-full">
                                    <a href="mailto:info@realta.media" className="text-xl font-display font-medium text-zinc-300 hover:text-brand-500 transition-colors tracking-wide whitespace-nowrap">info@realta.media</a>
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-1 font-bold">Click to contact</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="h-px w-full bg-white/20"></div>
             </div>
             
             {/* Bottom Action Buttons */}
             <div className="flex justify-end gap-6 items-center pt-8">
                <NavLink to="/booking"><Button variant="outline" size="sm" className="h-14 px-10 text-xs">Book Now</Button></NavLink>
                <NavLink to="/blog"><Button variant="outline" size="sm" className="h-14 px-10 text-xs">Our Blog</Button></NavLink>
                <NavLink to="/pre-shoot-guide"><Button variant="outline" size="sm" className="h-14 px-10 text-xs">Pre-Shoot Guide</Button></NavLink>
             </div>
          </div>
        </div>
        
        {/* Footer Bottom Metadata */}
        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-center md:justify-between items-center gap-6 text-center md:text-left">
          <p className="text-zinc-400 text-[10px] uppercase tracking-widest font-bold">© {new Date().getFullYear()} Realta Media Group. All Rights Reserved.</p>
          <div className="flex flex-col md:flex-row-reverse items-center gap-6 md:gap-6">
            <div className="flex space-x-8">
              <NavLink to="/privacy" className="text-zinc-400 hover:text-white text-[10px] uppercase tracking-widest font-bold transition-colors">Privacy</NavLink>
              <NavLink to="/terms" className="text-zinc-400 hover:text-white text-[10px] uppercase tracking-widest font-bold transition-colors">Terms</NavLink>
            </div>
            <div className="md:w-[150px] flex justify-start">
              <NavLink to="/admin/login" className="text-zinc-800 hover:text-brand-500 text-[10px] uppercase tracking-widest font-bold transition-colors">Admin</NavLink>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};